import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import Link from 'next/link'
import { HiChevronRight } from 'react-icons/hi'
import SearchExampleChips from './SearchExampleChips'
import { HIGHLIGHTED_CONTENT, QUICK_ACCESS_SHORTCUTS } from './searchShortcuts'

type SearchEmptyStateProps = {
  onExampleSelect: (_query: string) => void
}

/** Small uppercase label above each block of the empty state. */
const BlockTitle = ({ children }: { children: string }) => (
  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
    {children}
  </h3>
)

/** A 4-column grid of icon tiles for the most used pages. */
const QuickAccessGrid = () => (
  <ul className="grid grid-cols-4 gap-2">
    {QUICK_ACCESS_SHORTCUTS.map(({ name, href, icon: Icon }) => (
      <li key={href}>
        <Link
          href={getNativeReaderHref(href)}
          className="flex h-full flex-col items-center gap-1.5 rounded-xl px-1 py-3 text-center transition-colors hover:bg-surface-strong active:bg-surface-strong"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon size={18} aria-hidden />
          </span>
          <span className="text-xs font-medium leading-tight text-main">{name}</span>
        </Link>
      </li>
    ))}
  </ul>
)

/** A short list of often-read surahs and duas, plus a link to all duas. */
const HighlightedList = () => (
  <ul className="overflow-hidden rounded-2xl border border-main">
    {HIGHLIGHTED_CONTENT.map(({ name, href }) => (
      <li key={href} className="border-b border-main last:border-b-0">
        <Link
          href={getNativeReaderHref(href)}
          className="flex min-h-12 items-center justify-between px-4 py-3 text-[15px] font-medium text-main transition-colors hover:bg-surface-strong active:bg-surface-strong"
        >
          {name}
          <HiChevronRight size={18} className="text-muted" aria-hidden />
        </Link>
      </li>
    ))}
  </ul>
)

/**
 * What the search modal shows before the user types: quick access tiles, example
 * queries that teach the search syntax, and a few often-read surahs and duas.
 */
const SearchEmptyState = ({ onExampleSelect }: SearchEmptyStateProps) => (
  <div className="space-y-6">
    <section>
      <BlockTitle>Hızlı erişim</BlockTitle>
      <QuickAccessGrid />
    </section>
    <section>
      <BlockTitle>Şöyle arayabilirsin</BlockTitle>
      <SearchExampleChips onSelect={onExampleSelect} />
    </section>
    <section>
      <BlockTitle>Sık okunanlar</BlockTitle>
      <HighlightedList />
      <Link
        href="/dualar"
        className="mt-2 inline-block px-1 py-2 text-sm font-medium text-primary"
      >
        Tüm dualar →
      </Link>
    </section>
  </div>
)

export default SearchEmptyState
